import { Link } from "react-router-dom";
import Navbar from "../NavBar";
import Footer from "../Footer";

const environments = [
  {
    id: "public-sector",
    title: "Public Sector & Government",
    intro: "Systems supporting national and state-level operations across multiple agencies.",
    points: [
      "Multi-agency coordination and reporting",
      "Public-facing platforms and citizen interaction",
      "Regulatory oversight and compliance workflows",
      "High-visibility, high-scrutiny environments",
    ],
    image: "/images/surwash/cs_ntip.jpg",
    repWork: [
      { label: "National travel governance platforms", to: "/projects/1" },
      { label: "State-level project coordination systems", to: "/projects/7" },
      { label: "Federal housing program reporting systems", to: "/projects/8" },
      { label: "Institutional document governance systems", to: "/projects/3" },
    ],
  },
  {
    id: "healthcare",
    title: "Healthcare & Public Health",
    intro: "Time-critical systems for surveillance, reporting, and coordinated response.",
    points: [
      "Real-time data ingestion and analysis",
      "Multi-source reporting across labs and institutions",
      "Operational dashboards for decision-makers",
      "Systems operating under public and policy pressure",
    ],
    image: "/images/surwash/cs_lagosepid.jpg",
    repWork: [
      { label: "Pandemic response coordination platforms", to: "/projects/4" },
      { label: "Health project management systems", to: "/projects/7" },
    ],
  },
  {
    id: "environment",
    title: "Environment & Climate Systems",
    intro: "Platforms supporting environmental governance and emerging market infrastructure.",
    points: [
      "Carbon registries and credit lifecycle systems",
      "Environmental compliance and reporting",
      "Transparency and public accountability layers",
      "MRV (Monitoring, Reporting, Verification) systems",
    ],
    image: "/images/lagoscarbon/WhatsApp%20Image%202026-05-02%20at%2013.16.13.jpeg",
    repWork: [
      { label: "Carbon registry and credit lifecycle platforms", to: "/projects/9" },
      { label: "Environmental regulatory compliance systems", to: "/projects/5" },
    ],
  },
  {
    id: "energy",
    title: "Energy & Regulated Markets",
    intro: "Systems operating across complex, regulated value chains.",
    points: [
      "Compliance tracking across production and distribution",
      "Transaction monitoring and verification",
      "Multi-stakeholder coordination (regulators, operators, intermediaries)",
      "Auditability across the lifecycle of operations",
    ],
    image: "/images/surwash/cs_surcharge.jpg",
    repWork: [
      { label: "Fuel surcharge and levy compliance platforms", to: "/projects/2" },
    ],
  },
  {
    id: "finance",
    title: "Finance & Revenue Systems",
    intro: "High-trust systems where accuracy, traceability, and control are critical.",
    points: [
      "Payment and transaction workflows",
      "Audit-ready data and reporting",
      "Revenue tracking and reconciliation",
      "Institutional financial oversight",
    ],
    image: "/images/surwash/cs_lasepa.jpg",
    repWork: [
      { label: "Regulatory revenue and billing platforms", to: "/projects/5" },
      { label: "Levy collection and compliance audit systems", to: "/projects/2" },
    ],
  },
  {
    id: "education",
    title: "Education & Institutional Operations",
    intro: "ERP and governance systems supporting structured institutional management.",
    points: [
      "Budgeting and financial planning",
      "Asset and operational management",
      "Reporting and decision-support systems",
      "Multi-department coordination",
    ],
    image: "/images/surwash/cs_tetfund.jpg",
    repWork: [
      { label: "University ERP and governance platforms", to: "/projects/6" },
    ],
  },
];

const consistentChallenges = [
  "Multiple stakeholders",
  "Unclear or fragmented workflows",
  "Need for validation and control",
  "Requirement for traceability and reporting",
  "Systems that must perform under real conditions",
];

const meansForYou = [
  "Coordination across teams, agencies, or organisations",
  "Compliance, auditability, or regulatory oversight",
  "High-volume operations or transactions",
  "Reporting that must be accurate and timely",
  "Systems that must hold under pressure",
];

export default function Environments() {
  return (
    <section className="w-full relative bg-white text-gray-800">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[460px] bg-[#101722] text-white overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: "url('/images/sliders/_governance.jpg')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#101722] via-[#101722]/90 to-[#101722]/60" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-20 py-28">
          <p className="text-sm uppercase tracking-[0.2em] text-[#44c4bd] font-semibold mb-5">
            Operational Environments
          </p>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight max-w-4xl">
            Where Our Systems Are Deployed
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-white/75 leading-relaxed">
            We build systems for environments where structure, control, and accountability are
            critical. These are not "industries" in the conventional sense.
          </p>
          <p className="mt-3 max-w-3xl text-white/60 leading-relaxed">
            They are operational contexts where systems must function reliably across
            stakeholders, workflows, and real-world constraints.
          </p>
        </div>
      </section>

      {/* How to Read This Page */}
      <div className="bg-[#f5f7fa] border-b border-gray-200 px-6 lg:px-20 py-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row sm:items-center gap-3">
          <span className="text-[#007681] font-bold text-sm uppercase tracking-widest shrink-0">
            How to Read This Page
          </span>
          <span className="hidden sm:block text-gray-300">|</span>
          <p className="text-gray-600 text-sm leading-relaxed">
            Each environment reflects a class of systems we design and deliver — defined not
            by sector labels, but by the level of coordination, governance, and execution
            required.
          </p>
        </div>
      </div>

      {/* Alternating environment sections */}
      {environments.map((env, idx) => (
        <EnvironmentRow key={env.id} env={env} idx={idx} />
      ))}

      {/* Across All Environments */}
      <section className="bg-[#1C1F2B] text-white py-20 px-6 lg:px-20">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-14 items-start">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] text-[#44c4bd] font-bold mb-4">
              Across All Environments
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold mb-5 leading-tight">
              While these contexts differ, the underlying system challenges are consistent.
            </h2>
            <p className="text-white/60 leading-relaxed">
              This is where our work is focused.
            </p>
          </div>
          <ul className="space-y-3 mt-2">
            {consistentChallenges.map((item) => (
              <li key={item} className="flex items-start gap-3 border border-white/10 p-4">
                <span className="text-[#44c4bd] font-bold mt-0.5 shrink-0">▸</span>
                <span className="text-white/85 font-medium">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* What This Means for You */}
      <section className="bg-white py-16 px-6 lg:px-20 border-t border-gray-100">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-14 items-start">
          <div>
            <p className="text-sm uppercase tracking-[0.18em] text-[#007681] font-bold mb-3">
              What This Means for You
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold mb-5">
              If your environment involves any of these, the challenge is structural.
            </h2>
            <p className="text-gray-500 leading-relaxed">
              It is not industry-specific. The same system disciplines apply whether you are
              running a national agency, a regulated market, or a donor-backed programme.
            </p>
          </div>
          <div className="space-y-3">
            {meansForYou.map((item) => (
              <div key={item} className="flex items-start gap-3 bg-[#f5f7fa] border-l-4 border-[#007681] p-4">
                <span className="text-[#007681] font-bold shrink-0 mt-0.5">▸</span>
                <span className="text-gray-800 font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bridge to Action */}
      <section className="bg-[#101722] text-white py-20 px-6 lg:px-20">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-sm uppercase tracking-[0.2em] text-[#44c4bd] font-semibold mb-5">
            Bridge to Action
          </p>
          <h2 className="text-3xl md:text-4xl font-bold max-w-2xl mx-auto leading-tight mb-4">
            Systems don't fail because of sector.
          </h2>
          <p className="text-white/60 text-lg max-w-xl mx-auto mb-12">
            They fail because structure, governance, and execution are misaligned.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/framework"
              className="bg-[#E4002B] text-white px-8 py-4 font-semibold text-sm hover:bg-red-700 transition-colors duration-200"
            >
              Systems Execution Framework →
            </Link>
            <Link
              to="/projects"
              className="border border-white text-white px-8 py-4 font-semibold text-sm hover:bg-white hover:text-[#101722] transition-colors duration-200"
            >
              Selected Case Studies →
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </section>
  );
}

function EnvironmentRow({ env, idx }) {
  const isReversed = idx % 2 === 1;

  return (
    <section className={`py-20 px-6 lg:px-20 border-t border-gray-100 ${idx % 2 === 0 ? "bg-white" : "bg-[#f5f7fa]"}`}>
      <div className={`max-w-6xl mx-auto flex flex-col ${isReversed ? "lg:flex-row-reverse" : "lg:flex-row"} gap-12 items-center`}>

        {/* Image */}
        <div className="w-full lg:w-1/2">
          <img
            src={env.image}
            alt={env.title}
            className="w-full h-72 lg:h-96 object-cover rounded-sm shadow-md"
          />
        </div>

        {/* Content */}
        <div className="w-full lg:w-1/2">
          <p className="text-sm uppercase tracking-[0.18em] text-[#007681] font-bold mb-3">
            {env.title}
          </p>
          <h2 className="text-2xl lg:text-3xl font-bold mb-4 leading-snug">{env.intro}</h2>
          <ul className="space-y-2 mb-8">
            {env.points.map((pt) => (
              <li key={pt} className="flex items-start gap-2 text-gray-600 text-sm">
                <span className="text-[#E4002B] mt-0.5 shrink-0">▸</span>
                <span>{pt}</span>
              </li>
            ))}
          </ul>

          {/* Representative Work */}
          <div className="border-t border-gray-200 pt-5">
            <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-3">
              Representative Work
            </p>
            <ul className="space-y-1.5">
              {env.repWork.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="text-sm text-gray-500 hover:text-[#E4002B] transition-colors duration-200 flex items-center gap-1.5 group"
                  >
                    <span className="text-gray-300 group-hover:text-[#E4002B] transition-colors">→</span>
                    <span className="underline underline-offset-2 decoration-gray-200 group-hover:decoration-[#E4002B]">
                      {item.label}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
